import { serviceOrderApi } from "@features/cx/shared/apis/serviceOrderTab";
import {
  useJobCardPriority,
  useServiceOrderChoices,
} from "@features/cx/shared/hooks/useServiceOrder";
import { queryClient } from "@shared/api/queries/queryClient";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Info, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
interface ServiceOrderDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  dialogTitle?: string;
  dialogDescription?: string;
  defaultType?: string;
  defaultDate?: string;
  defaultRemarks?: string;
  submitButtonText?: string;
  consumerId: string;
  consumerName: string;
  consumerAccountNumber: string;
  requestId: string;
  remoteUtilityId: number;
  onSuccess?: () => void;
}

export function ServiceOrderDialogue({
  isOpen,
  onClose,
  dialogTitle = "Create New Service Order",
  dialogDescription = "Create a new service order for the selected consumer.",
  defaultType = "",
  defaultDate = "",
  defaultRemarks = "",
  consumerId,
  consumerName,
  consumerAccountNumber,
  requestId,
  remoteUtilityId,
  onSuccess,
  submitButtonText = "Add Service Order",
}: ServiceOrderDialogueProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use the service order choices hook
  const { types: serviceOrderTypes, isLoading: isLoadingChoices } =
    useServiceOrderChoices(remoteUtilityId);

  const { data: priorityData } = useJobCardPriority();
  const priorityOptions =
    priorityData?.map((priority) => ({
      value: priority.key,
      label: priority.value,
    })) || [];

  // Use API data if available, otherwise use fallback
  const availableServiceOrderTypes =
    serviceOrderTypes?.length > 0 ? serviceOrderTypes : [];

  // Define form fields using FormService structure
  const serviceOrderFields: FormField[] = useMemo(
    () => [
      {
        name: "sop",
        label: "Service Order Type",
        type: "select",
        required: true,
        placeholder: "Select type",
        disabled: isSubmitting || isLoadingChoices,
        options: availableServiceOrderTypes.map((orderType, index) => ({
          value: orderType.id.toString(),
          label: orderType.name,
          disabled: false,
        })),
        defaultValue: defaultType,
        helperText: "Select the type of service order to create",
        fullWidth: true,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-red-600 text-xs mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "scheduledDate",
        label: "Scheduled Date",
        type: "datepicker",
        required: true,
        disabled: isSubmitting,
        helperText: "Select the date when the service order should be executed",
        fullWidth: true,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-red-600 text-xs mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "priority",
        label: "Select Priority",
        type: "select",
        required: true,
        helperText: "Select the Priority",
        options: priorityOptions,
        fullWidth: true,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-red-600 text-xs mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "remarks",
        label: "Remarks",
        type: "textarea",
        placeholder: "Add remarks or instructions",
        disabled: isSubmitting,
        defaultValue: defaultRemarks,
        rows: 3,
        helperText:
          "Optional remarks or special instructions for the service order",
        fullWidth: true,
        classes: {
          container: "w-full mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-2",
          textarea:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none",
          error: "text-red-600 text-xs mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
    ],
    [
      isSubmitting,
      isLoadingChoices,
      defaultType,
      defaultDate,
      defaultRemarks,
      availableServiceOrderTypes,
    ]
  );

  // Create form instance using FormService
  const serviceOrderForm = FormService.createForm({
    fields: serviceOrderFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      type: defaultType,
      scheduledDate: defaultDate,
      remarks: defaultRemarks,
    },
  });

  // Reset form when dialog opens/closes or default values change
  useEffect(() => {
    serviceOrderForm.reset();
    setIsSubmitting(false);
  }, [isOpen, defaultType, defaultDate, defaultRemarks, serviceOrderForm]);

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (formData: any) => {
      setIsSubmitting(true);

      try {
        // Create payload using FormService
        const payload = FormService.createPayload(formData, serviceOrderFields);

        // Prepare the service order data
        const serviceOrderData = {
          sop: Number(payload.sop),
          consumerSupportRequestId: Number(requestId),
          remoteUtilityId,
          requestAccountNo: consumerAccountNumber,
          requestId: Number(consumerId),
          requestUsername: consumerName,
          source: 1,
          requestAvailabilityDate: payload.scheduledDate,
          priority: payload.priority,
          remark: payload.remarks?.trim() || "",
        };

        // Create the service order using the API directly
        await serviceOrderApi.createServiceOrder(serviceOrderData);
        await queryClient.refetchQueries({
          queryKey: ["serviceOrders"],
        });
        onClose();
      } catch (error: any) {
        console.error("Failed to create service order:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      consumerId,
      requestId,
      remoteUtilityId,
      onSuccess,
      onClose,
      serviceOrderFields,
    ]
  );

  // Handle field changes for real-time updates
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any real-time validation or side effects here
    console.log(`Field ${fieldName} changed to:`, value);
  }, []);

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Custom footer with actions
  const renderCustomFooter = useCallback(
    () => (
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={() => serviceOrderForm.handleSubmit(handleFormSubmit)()}
          disabled={isSubmitting || isLoadingChoices}
          className="flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Creating...</span>
            </>
          ) : (
            <span>+ {submitButtonText}</span>
          )}
        </Button>
      </div>
    ),
    [
      isSubmitting,
      isLoadingChoices,
      submitButtonText,
      handleClose,
      serviceOrderForm,
      handleFormSubmit,
    ]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {/* Helping Text (Info Box) */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Info className="text-blue-500 h-5 w-5" />
            <span className="font-semibold text-blue-700">
              Service Order Information
            </span>
          </div>
          <div className="text-blue-700 ml-7">
            <div>Service orders can be created for various service types:</div>
            <ul className="list-disc ml-6 mt-2">
              <li>Meter installations for new connections</li>
              <li>Field inspections for service quality issues</li>
              <li>Technical repairs for infrastructure problems</li>
            </ul>
          </div>
        </div>

        {/* Dynamic Form using FormService */}
        <DynamicForm
          fields={serviceOrderFields}
          form={serviceOrderForm}
          onSubmit={handleFormSubmit}
          onFieldChange={handleFieldChange}
          loading={isSubmitting || isLoadingChoices}
          config={{
            showSubmitButton: false, // We'll use custom footer
            showResetButton: false,
            layout: "vertical",
            spacing: "medium",
          }}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2 }}
          renderCustomFooter={renderCustomFooter}
          className="space-y-4"
        />
      </DialogContent>
    </Dialog>
  );
}
